import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { resolveAssetUrl } from "@/lib/api";
import { Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CartDrawer = () => {
  const {
    items,
    open,
    setOpen,
    remove,
    updateQty,
    subtotal,
    email,
    setEmail,
    lineKey,
  } = useCart();
  const [loading, setLoading] = useState(false);

  const shipping = subtotal >= 100 ? 0 : subtotal > 0 ? 9 : 0;
  const total = subtotal + shipping;

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/checkout/session", {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size || null,
          color: i.color || null,
        })),
        origin_url: window.location.origin,
        email: email || null,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Checkout failed. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-zinc-950 border-l border-zinc-800 text-white p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-zinc-800">
          <SheetTitle className="font-heading text-2xl font-bold uppercase tracking-tight text-white">
            Your Gear Bag
          </SheetTitle>
          <SheetDescription className="text-xs text-zinc-500 tracking-widest uppercase">
            {items.length} item{items.length !== 1 ? "s" : ""} in your bag
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 ? (
            <div
              className="text-center py-12 text-zinc-500 text-sm tracking-wide"
              data-testid="cart-empty-state"
            >
              The bag is empty. Suit up.
            </div>
          ) : (
            items.map((it) => {
              const k = lineKey(it);
              return (
                <div
                  key={k}
                  className="flex gap-4 border-b border-zinc-900 pb-4"
                  data-testid={`cart-line-${it.product_id}`}
                >
                  <img
                    src={resolveAssetUrl(it.image_url)}
                    alt={it.name}
                    className="w-20 h-24 object-cover bg-zinc-900"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm uppercase tracking-wide leading-tight">
                      {it.name}
                    </h4>
                    {(it.size || it.color) && (
                      <p className="text-zinc-500 text-[10px] tracking-widest uppercase mt-1">
                        {[it.size, it.color].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="text-zinc-500 text-xs mt-1">
                      ${it.price.toFixed(2)}
                    </p>
                    <div className="flex items-center mt-2 gap-2">
                      <button
                        onClick={() => updateQty(k, it.quantity - 1)}
                        className="w-6 h-6 border border-zinc-700 flex items-center justify-center hover:bg-zinc-800"
                        aria-label="Decrease"
                        data-testid={`cart-qty-decrease-${it.product_id}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span
                        className="text-xs font-bold w-6 text-center"
                        data-testid={`cart-qty-value-${it.product_id}`}
                      >
                        {it.quantity}
                      </span>
                      <button
                        onClick={() => updateQty(k, it.quantity + 1)}
                        className="w-6 h-6 border border-zinc-700 flex items-center justify-center hover:bg-zinc-800"
                        aria-label="Increase"
                        data-testid={`cart-qty-increase-${it.product_id}`}
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => remove(k)}
                        className="ml-auto text-zinc-500 hover:text-red-500"
                        aria-label="Remove"
                        data-testid={`cart-remove-${it.product_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p
                    className="text-sm font-bold whitespace-nowrap"
                    data-testid={`cart-line-total-${it.product_id}`}
                  >
                    ${(it.price * it.quantity).toFixed(2)}
                  </p>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-zinc-800 px-6 py-5 space-y-3 bg-black">
          {items.length > 0 && (
            <input
              type="email"
              placeholder="Email for order updates (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-xs px-3 py-2 outline-none focus:border-zinc-500 placeholder:text-zinc-600 placeholder:tracking-wider"
              data-testid="cart-email-input"
            />
          )}
          <div className="flex justify-between text-xs uppercase tracking-widest text-zinc-400">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xs uppercase tracking-widest text-zinc-400">
            <span>Shipping</span>
            <span data-testid="cart-shipping">
              {shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}
            </span>
          </div>
          <div className="flex justify-between text-base font-heading font-bold uppercase tracking-tight pt-2 border-t border-zinc-800">
            <span>Total</span>
            <span data-testid="cart-total">${total.toFixed(2)}</span>
          </div>
          <button
            onClick={handleCheckout}
            disabled={loading || items.length === 0}
            className="w-full bg-white text-black font-heading font-bold text-base tracking-widest uppercase px-6 py-4 hover:bg-zinc-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="cart-checkout-btn"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} /> Processing
              </>
            ) : (
              "Secure Checkout"
            )}
          </button>
          <p className="text-[10px] text-zinc-600 text-center tracking-widest uppercase pt-1">
            Powered by Stripe. Built on Faith.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
